import React, { useCallback, useMemo, useRef, useState } from 'react';
import Avatar from './Avatar';
import CameraDialog from './CameraDialog';
import { AvatarPresetDialog, AvatarSourceDialog } from './AvatarPicker';
import styles from './Account.module.css';
import { IconCamera } from './AccountIcons';
import { AVATAR_ACCEPT, fileToAvatarDataUrl, ImageError } from '../../utils/imageResize';

/**
 * Phones and tablets hand the camera off to the OS, which is faster and takes
 * a better photo than anything in a web page. Desktops have no camera app to
 * hand off to, so they get an in-page preview instead — and only if the
 * browser exposes a camera at all.
 */
function cameraMode() {
  if (typeof window === 'undefined') return 'none';
  if (window.matchMedia?.('(pointer: coarse)').matches) return 'native';
  if (navigator.mediaDevices?.getUserMedia) return 'inline';
  return 'none';
}

/**
 * Upload, replace and remove the profile picture.
 *
 * The whole avatar is the trigger rather than a separate button: it is the
 * largest, most obvious target on the page, and the small camera badge exists
 * to say it is interactive at all. Tapping it asks where the picture should
 * come from, so the three sources do not have to sit on the page as three more
 * controls.
 */
export default function AvatarField({ name, avatarUrl, onUpload, onRemove, size = 92 }) {
  const fileInputRef = useRef(null);
  const captureInputRef = useRef(null);
  const [busy, setBusy] = useState(null); // 'upload' | 'remove' | null
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null); // 'source' | 'preset' | 'camera' | null

  const mode = useMemo(cameraMode, []);

  const open = useCallback(() => {
    setError('');
    setDialog('source');
  }, []);

  const save = useCallback(
    async (dataUrl) => {
      setBusy('upload');
      try {
        await onUpload(dataUrl);
        setDialog(null);
      } finally {
        setBusy(null);
      }
    },
    [onUpload]
  );

  const handleFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      // Reset immediately so choosing the same file twice still fires change.
      event.target.value = '';
      if (!file) return;

      setError('');
      // Close the chooser now rather than when the save lands, so progress is
      // shown on the avatar itself instead of behind a dialog.
      setDialog(null);
      try {
        await save(await fileToAvatarDataUrl(file));
      } catch (e) {
        setDialog(null);
        setError(
          e instanceof ImageError
            ? e.message
            : e?.response?.data?.error || 'Could not save that picture. Please try again.'
        );
      }
    },
    [save]
  );

  const handleRemove = useCallback(async () => {
    setError('');
    setBusy('remove');
    try {
      await onRemove();
      setDialog(null);
    } catch (e) {
      setDialog(null);
      setError(e?.response?.data?.error || 'Could not remove that picture. Please try again.');
    } finally {
      setBusy(null);
    }
  }, [onRemove]);

  const isBusy = busy !== null;

  return (
    <div className={styles.avatarField}>
      <button
        type="button"
        className={styles.avatarButton}
        onClick={open}
        disabled={isBusy}
        aria-haspopup="dialog"
        aria-label={avatarUrl ? 'Change your profile picture' : 'Add a profile picture'}
      >
        <Avatar name={name} src={avatarUrl} size={size} />
        <span className={styles.avatarBadge} aria-hidden="true">
          <IconCamera size={14} />
        </span>
        {busy === 'upload' ? <span className={styles.avatarSpinner} aria-hidden="true" /> : null}
      </button>

      <button type="button" className={styles.textButton} onClick={open} disabled={isBusy}>
        {busy === 'upload' ? 'Saving…' : busy === 'remove' ? 'Removing…' : avatarUrl ? 'Change' : 'Add photo'}
      </button>

      {error ? (
        <p className={styles.avatarError} role="alert">
          {error}
        </p>
      ) : null}

      <AvatarSourceDialog
        open={dialog === 'source'}
        onClose={() => setDialog(null)}
        hasImage={Boolean(avatarUrl)}
        canTakePhoto={mode !== 'none'}
        onTakePhoto={() => {
          if (mode === 'native') {
            captureInputRef.current?.click();
            return;
          }
          setDialog('camera');
        }}
        onChooseFile={() => fileInputRef.current?.click()}
        onChoosePreset={() => setDialog('preset')}
        onRemove={handleRemove}
      />

      <AvatarPresetDialog
        open={dialog === 'preset'}
        onClose={() => setDialog(null)}
        name={name}
        onSelect={save}
      />

      <CameraDialog open={dialog === 'camera'} onClose={() => setDialog(null)} onCapture={save} />

      <input
        ref={fileInputRef}
        type="file"
        accept={AVATAR_ACCEPT}
        className={styles.visuallyHidden}
        onChange={handleFile}
        tabIndex={-1}
      />
      {/* Separate input: `capture` is what opens the camera rather than the
          photo library, and the same input cannot do both. */}
      <input
        ref={captureInputRef}
        type="file"
        accept="image/*"
        capture="user"
        className={styles.visuallyHidden}
        onChange={handleFile}
        tabIndex={-1}
      />
    </div>
  );
}
